import {setKelvin} from 'three/addons/utils/ColorUtils';
import {
	Box3,
	Color,
	MeshPhysicalMaterial,
	NoColorSpace,
	Object3D,
	RepeatWrapping,
	TextureLoader,
	Vector2,
	Vector3
} from 'three';

import {colorStringToInt, degToRad, getLightColorFromTemperature, getQuaternionByAngle, isString, radToDeg} from '../components/visual/iomo3d-utils';
import {iomoMaterials} from '../iomoMaterial';
import {iomoLightDistributionOptions} from '../iomoModel';


const ucFirst = (s) => s.substring(0, 1).toUpperCase() + s.substring(1);

const conf = {
	keySep: '__',

	maxHeadsAmount: 8,

	headLinkRegExp: /head_link/i,
	lightLinkRegExp: /light_link/i,
	topLinkRegExp: /top_link/i,
	pointLightRegExp: /PointLight/i,
	spotLightRegExp: /SpotLight/i,
	meshNameSuffix: '_Mesh',
	armNameSuffix: '_Arm',
	baseNameSuffix: '_Base',
	capAsymRegExp: /CapAsym/i,
	wallmountRegExp: /Wallmount/i,

	metalMaterial: 'steel',
	woodMaterial: 'wood',

	headParts: {
		capShort: {
			regExp: /CapShort$/i,
			condition: (head) => (head.cap === 'short'),
		},
		capLong: {
			regExp: /capLong/i,
			condition: (head) => (head.cap === 'long'),
		},
		capAsymmetric: {
			regExp: /capAsym/i,
			condition: (head) => (head.cap === 'asymmetric'),
		},
		honeycomb: {
			regExp: /Honeycomb/i,
			condition: (head) => (head.modifier === 'honeycomb'),
		},

	},

	defaultHousingSize: '195',
	defaultOpticalSystem: 'Comfort',

	reflectorEmissionIntensity: 0.9,

	defaultLightPower: 1800,
	defaultColourTemperature: '5500K',

	headPitch: 20, // deg
	headRoll: 0, // deg

	modelOnCollisionColor: '#AA0000',

	staticViewMaterialColor: 0x404040,
};


class IomoLuminaire {
	static conf = conf;

	static poleSizes = {};

	static sharedModels = {
		head: {},
	};

	static models = {};

	static sharedModelPatterns = [
		{type: 'head', pattern: /^(?<id>(250Comfort)|(195Comfort)|(160Comfort)|(250Performance)|(195Performance)|(160Performance))$/i, storageKeys: ['id']},
	];

	static modelPatterns = [];

	static designName = '';

	static lightLinkOffsets = {};
	static headLinkOffsets = {};
	static internalOffsets = null;

	static requireCutouts = false;
	static hasBrackets = true;
	static singleBracket = false;
	static supportLayouts = true;
	static checkCollisions = true;
	static defaultConfig = {};


	configValues = {};
	pole = null;
	branches = [];
	branchHorizontalOffsets = [];
	branchTargetQuaternions = [];
	branchTargetHeadQuaternions = [];
	branchTargetRollQuaternions = [];


	constructor(configValues = null) {
		if (configValues !== null) {
			this.update(configValues);
			if (this.constructor.hasBrackets) {
				this.updateHeadsPositions();
			}
		}
	}

	static recognizeModels(models, staticView = false) {
		for (let model of models) {
			if (!('modelType' in model.userData)) {
				const recognizedModel = this.recognizeModel(model);
				if (recognizedModel) {
					model = recognizedModel;
					model.traverse((obj) => {
						if (obj.isMesh) {
							if (obj.geometry && (obj.name.toLowerCase().includes('prismenscheibe'))) {
								//obj.geometry.computeTangents();
							} else {
								if (obj.geometry) {
									obj.geometry.computeVertexNormals();
								}
							}
							if (!obj.userData.headPart && obj.userData.materialType !== conf.woodMaterial) {
								//obj.material = this.getRealisticMaterial(staticView);
							}
							obj.castShadow = true;
							obj.receiveShadow = true;
							if (obj.isMesh && obj.name.toLowerCase().includes('ux')) {
								obj.visible = false;
							}
						}
					});
				}
			}
		}
		//console.log('recognizedModels', models);
		if (this.hasBrackets && !this.singleBracket) {
			this.calculateInternalOffsets();
		}
	}

	static recognizeModel(model) {
		let recognizedModel = this.recognizingModel(model, this.sharedModelPatterns, this.sharedModels);
		if (!recognizedModel) {
			recognizedModel = this.recognizingModel(model, this.modelPatterns, this.models);
		}
		//console.log('recognizedModel', recognizedModel);
		return recognizedModel;
	}

	static recognizingModel(model, patterns, store) {
		for (const entry of patterns) {
			const result = this.matchModel(model, patterns);
			if (result) {
				model = this.processModel(model, result.entry, result.matches);
				this.storeModel(store, model, result.entry, result.matches);
				return model;
			}
		}
		return null;
	}

	static matchModel(model, patterns) {
		const name = model.name.trim();
		for (const entry of patterns) {
			const matches = name.match(entry.pattern);
			if (matches) {
				return {
					entry: entry,
					matches: matches
				};
			}
		}
		return false;
	}

	static processModel(model, entry, matches) {
		//console.log('processModel', model, entry, matches);
		model.userData.modelType = entry.type;
		model.userData.design = this.designName;
		const keys = [];
		for (const key of entry.storageKeys) {
			const keyIsString = isString(key);
			const keyValue = keyIsString ? key : key.key;
			let value = null;
			if (keyIsString) {
				if (keyValue in matches.groups && matches.groups[keyValue] !== null) {
					value = matches.groups[keyValue].toLowerCase();
				}
			} else {
				value = key.value;
			}
			if (value !== null) {
				model.userData[keyValue] = value;
				keys.push(value);
			}
		}
		const key = keys.join(conf.keySep);
		model.userData.key = key;
		const method = 'process' + ucFirst(entry.type) + 'Model';
		if (method in this) {
			model = this[method](model, entry, matches);
		}
		return model;
	}

	static storeModel(store, model, entry, matches) {
		if (!(entry.type in store)) {
			store[entry.type] = {};
		}
		const keys = [];
		for (const key of entry.storageKeys) {
			const keyIsString = isString(key);
			const keyValue = keyIsString ? key : key.key;
			let value = null;
			if (keyIsString) {
				if (keyValue in matches.groups && matches.groups[keyValue] !== null) {
					value = matches.groups[keyValue].toLowerCase();
				}
			} else {
				value = key.value;
			}
			if (value !== null) {
				keys.push(value);
			}
		}
		const key = keys.join(conf.keySep);
		store[entry.type][key] = model;
	}

	static calculateInternalOffsets() {
		this.internalOffsets = {};
		console.log('calculateInternalOffsets', this.headLinkOffsets, this.lightLinkOffsets);
		for (const bracketKey of Object.keys(this.headLinkOffsets)) {
			const bracketEntry = this.headLinkOffsets[bracketKey];
			for (const headKey of Object.keys(this.lightLinkOffsets)) {
				const headEntry = this.lightLinkOffsets[headKey];
				const key = bracketKey + conf.keySep + headKey;
				this.internalOffsets[key] = {
					x: bracketEntry.x, // + headEntry.x,
					y: bracketEntry.y, // + headEntry.y
				};
			}
		}
		console.log('this.internalOffsets', this.internalOffsets);

	}

	static processHeadModel(model, entry, matches) {
		let foundLightLink = false;
		model.userData.hoverable = true;
		model.traverse((subChild) => {
			subChild.userData.hoverable = true;
			if (!foundLightLink && subChild.name.match(conf.lightLinkRegExp)) {
				foundLightLink = true;
				this.lightLinkOffsets[model.userData.key] = {x: subChild.position.x, y: subChild.position.y};
			}

			for (const partName of Object.keys(conf.headParts)) {
				const headPart = conf.headParts[partName];
				if (subChild.name.match(headPart.regExp)) {
					subChild.visible = false;
					subChild.userData.modelType = 'headPart';
					subChild.userData.headPart = partName;
					const materialMethod = 'get' + ucFirst(partName) + 'Material';
					if (materialMethod in this) {
						subChild.material = this[materialMethod]();
					}
				}
			}
		});
		return model;
	}

	static processBracketModel(model, entry, matches) {
		// we need the bracket to be a nested object (object + mesh) in order to simplify the
		// horizontal movement and inclination for conical poles
		console.log('processBracketModel', model, entry, matches);
		if (model.isMesh) {
			const mesh = model;
			model = new Object3D();
			model.name = mesh.name;
			model.position.copy(mesh.position);
			model.userData = {...mesh.userData};
			mesh.position.set(0, 0, 0);
			mesh.name = model.name + conf.meshNameSuffix;
			model.add(mesh);
		}
		let foundHeadLink = false;
		if (!this.singleBracket) {
			model.userData.hoverable = true;
		}
		model.traverse((subChild) => {
			if (!this.singleBracket) {
				subChild.userData.hoverable = true;
			}
			if (!this.singleBracket && !foundHeadLink && subChild.name.match(conf.headLinkRegExp)) {
				//console.log('foundHeadLink', model.userData.key);
				foundHeadLink = true;
				this.headLinkOffsets[model.userData.key] = {x: subChild.position.x, y: subChild.position.y};
			}
		});
		return model;
	}

	/*
	static getRealisticMaterial(staticView = false) {
		let material;
		if (staticView) {
			material = new MeshPhysicalMaterial({
				clearcoat: 0,
				metalness: 0,
				roughness: 1,
				color: conf.staticViewMaterialColor
			});
		} else {
			const normalMap3 = new CanvasTexture( new FlakesTexture());
			normalMap3.wrapS = RepeatWrapping;
			normalMap3.wrapT = RepeatWrapping;
			normalMap3.repeat.x = 10;
			normalMap3.repeat.y = 6;
			normalMap3.anisotropy = 16;

			material = new MeshPhysicalMaterial({
				clearcoat: 0.5,
				clearcoatRoughness: 0.1,
				metalness: 0.6,
				roughness: 0.5,
				color: 0x666666,
				normalMap: normalMap3,
				normalScale: new Vector2(0.005, 0.005)
			});
			material.userData.colored = true;
		}
		return material;
	}
	*/


	static getCameraMaterial(staticView = false) {
		const material = new MeshPhysicalMaterial({
			ior: 2,
			reflectivity: 1,
			iridescence: 0,
			sheen: 0,
			clearcoat: 1,
			clearcoatRoughness: 0,
			metalness: 0,
			roughness: 0,
			specularIntensity: 1,
			specularColor: 0xFFFFFF,
			transmission: 0,
			color: 0x222228,
		});
		return material;
	}


	static isHeadPartVisible(headPart, headConfigValues) {
		return conf.headParts[headPart].condition(headConfigValues);
	}

	static isHeadSmall(model) {
		return model.userData.id === '160Comfort' || model.userData.id === '160Performance';
	}


	static getHeadModel(configValues, headConfigValues) {
		const headId =
			(headConfigValues.housingSize ? headConfigValues.housingSize : (configValues.housingSize ? configValues.housingSize : conf.defaultHousingSize)) +
			(headConfigValues.opticalSystem ? headConfigValues.opticalSystem : (configValues.opticalSystem ? configValues.opticalSystem : conf.defaultOpticalSystem))
				.toLowerCase().trim();
		return this.sharedModels.head[headId];
	}


	static getPoleModel(configValues) {
		// console.log('getPoleModel', configValues, this.getPoleId(configValues), this.models.pole);
		return this.models.pole[this.getPoleId(configValues)];
	}

	static getBracketModel(configValues, index, headConfigValues) {
		// const bracketId = this.getBracketId(configValues, index);
		// bracket is matching housing size
		const bracketId = (headConfigValues.housingSize ? headConfigValues.housingSize : (configValues.housingSize ? configValues.housingSize : conf.defaultHousingSize)).trim();
		return this.models.bracket[bracketId];
	}

	// to be overriden
	static getPoleId(configValues) {
	}

	// to be overriden
	static getBracketId(configValues, index) {
	}

	static getPoleColor(configValues) {
		return configValues.color ?? '#909090';
	}

	static getBracketColor(configValues) {
		return configValues.color ?? '#909090';
	}

	static getHeadColor(configValues) {
		return configValues.color ?? '#909090';
	}

	static getColorForLightTuning(configValues) {
		return this.getPoleColor(configValues);
	}

	static getBracketHorizontalOffset(configValues, height, index) {
		// newB / newH = b / h;
		const entry = this.poleSizes[this.getPoleId(configValues)];
		const newH = entry.maxHeight - height;
		const newB = entry.ratio * newH;
		return newB + entry.xBaseOffset;
	}

	static calculateOffsets(configValues, heads, apply = false) {
		const housingSize = configValues.housingSize ? configValues.housingSize : conf.defaultHousingSize;
		const opticalSystem = configValues.opticalSystem ? configValues.opticalSystem : conf.defaultOpticalSystem;

		const offsets = [];
		if (!this.internalOffsets) {
			return offsets;
		}
		const miniConf = {...configValues, housingSize: housingSize, opticalSystem: opticalSystem, heads: heads};
		const poleId = this.getPoleId(miniConf);
		// console.log('calculateOffsets getPoleId', poleId, miniConf);
		const poleEntry = this.poleSizes[poleId];
		if (!poleEntry) {
			return offsets;
		}
		const verticalAngle = degToRad(poleEntry.verticalAngle);
		for (let i = 0; i < heads.length; i++) {
			const head = heads[i];
			const bracketId = this.getBracketId(miniConf, i);
			const singleHousingSize = (head.housingSize ? head.housingSize : housingSize).trim();
			const verticalOffset = this.calculateVerticalOffset(verticalAngle, bracketId, singleHousingSize, opticalSystem);
			const actualHeight = head.height - verticalOffset;
			const la = this.calculateLaValue(poleEntry.height, actualHeight);
			offsets[i] = {
				verticalOffset: Math.round(verticalOffset * 1000) / 1000,
				actualHeight: Math.round(actualHeight * 1000) / 1000,
				la: Math.round(la * 1000) / 1000
			};
			if (apply) {
				head.la = offsets[i].la;
			}
		}
		return offsets;
	}

	static calculateOffsetsByHeight(configValues, height, housingSize, opticalSystem, heads, index) {
		const {poleHeight, bracket} = configValues;
		const miniConf = {...configValues, housingSize: housingSize, opticalSystem: opticalSystem, heads: heads};
		const bracketId = this.getBracketId(miniConf, index);
		//console.log('calculateOffsetsByHeight', miniConf, bracketId);
		const poleId = this.getPoleId(miniConf);
		console.log('calculateOffsetsByHeight getPoleId', poleId, miniConf);
		const poleEntry = this.poleSizes[poleId];
		const verticalAngle = degToRad(poleEntry.verticalAngle);
		const verticalOffset = this.calculateVerticalOffset(verticalAngle, bracketId, housingSize, opticalSystem);
		const actualHeight = height - verticalOffset;
		const la = this.calculateLaValue(poleEntry.height, actualHeight);
		return {
			verticalOffset: Math.round(verticalOffset * 1000) / 1000,
			actualHeight: Math.round(actualHeight * 1000) / 1000,
			la: Math.round(la * 1000) / 1000
		};

	}

	static calculateVerticalOffset(verticalAngle, bracketId, housingSize, opticalSystem) {
		const id = (bracketId + conf.keySep + housingSize + opticalSystem).toLowerCase();
		const entry = this.internalOffsets[id];
		//console.log('calculateVerticalOffset', verticalAngle, bracketId, housingSize, opticalSystem, id, this.internalOffsets);
		let verticalOffset = null;
		if (verticalAngle > 0) {
			const a = entry.x * Math.sin(verticalAngle);
			const b = entry.y * Math.cos(verticalAngle);
			verticalOffset = a + b;
		} else {
			verticalOffset = entry.y;
		}
		return verticalOffset;
	}


	// ------------------------------------- INSTANCE METHODS >

	update(configValues) {
		// console.log('update', configValues);
		const self = this.constructor;

		if (this.normalMap === null) {
			this.normalMap = new TextureLoader().load('/line-assets/iomo3d/noiseNormalMap.png');
			this.normalMap.colorSpace = NoColorSpace;
			this.normalMap.wrapS = RepeatWrapping;
			this.normalMap.wrapT = RepeatWrapping;
			this.normalMap.repeat.x = 10;
			this.normalMap.repeat.y = 10;
		}

		// const verticalOffsetsReady = verticalOffsets.isReady();

		// if (design.hasBrackets && !design.singleBracket) {
		// 	if (!verticalOffsetsReady) {
		// 		verticalOffsets.setInternalOffsets(this.internalOffsets);
		// 	}

		this.verticalOffsets = self.calculateOffsets(configValues, configValues.heads);

		// 	if (!verticalOffsetsReady) {
		// 		store.dispatch(setIomoHeadsData({heads: this.verticalOffsets.map((head) => ({la: head.la}))}));
		// 		return;
		// 	}
		// }

		const poleHeightChanged = this.configValues.poleHeight !== configValues.poleHeight;
		const selectedHeadChanged = this.configValues.selectedHead !== configValues.selectedHead;
		const selectedHeadHeightChanged =
			!selectedHeadChanged &&
			configValues.selectedHead !== null &&
			this.configValues.heads &&
			this.configValues.heads.length &&
			this.configValues.heads[configValues.selectedHead].height !== configValues.heads[configValues.selectedHead].height
		;
		const selectedHeadAngleChanged =
			!selectedHeadChanged &&
			!selectedHeadHeightChanged &&
			configValues.selectedHead !== null &&
			this.configValues.heads &&
			this.configValues.heads.length &&
			this.configValues.heads[configValues.selectedHead].angle !== configValues.heads[configValues.selectedHead].angle
		;
		const flags = {
			poleHeightChanged: poleHeightChanged,
			selectedHeadChanged: selectedHeadChanged,
			selectedHeadHeightChanged: selectedHeadHeightChanged,
			selectedHeadAngleChanged: selectedHeadAngleChanged,
		};

		flags.poleChanged = this.createOrUpdatePole(configValues);
		if (poleHeightChanged || this.isFeatureChanged(configValues, 'materialPole')) {
			//this.setColor(this.pole, self.getPoleColor(configValues), false);
			const exclude = ['Prismenscheibe', 'TIR', 'Reflektor', 'Innenreflektor', 'Light', 'Abschlussscheibe', 'Honeycomb', 'light', 'UX', 'GFX', 'Zentralabdeckung', 'Wall'];
			let materialPole = iomoMaterials.blackMatte.material;
			if (configValues.materialPole) materialPole = configValues.materialPole;

			this.updateMaterialParameter(this.pole, '', materialPole, exclude, configValues);
		}

		if (self.hasBrackets) {
			const poleId = self.getPoleId(configValues);
			const verticalAngle = degToRad(self.poleSizes[poleId].verticalAngle);

			for (let i = 0; i < configValues.heads.length; i++) {
				this.createOrUpdateLightBranch(i, configValues);
				const branchConfig = configValues.heads[i];
				const branch = this.branches[i];
				if (((self.singleBracket && i === 0) || (!self.singleBracket && branchConfig.active)) && branch.bracket.parent !== this.pole) {
					this.pole.add(branch.bracket);
				} else if (!self.singleBracket && !branchConfig.active) {
					// branch.bracket.visible = false;
					if (branch && branch.bracket && branch.bracket.parent === this.pole) {
						this.pole.remove(branch.bracket);
					}
				}
				if (!self.singleBracket) {
					this.branchHorizontalOffsets[i] = self.getBracketHorizontalOffset(configValues, this.verticalOffsets[i].actualHeight, i);
					this.branchTargetQuaternions[i] = getQuaternionByAngle(0, degToRad(branchConfig.angle), verticalAngle);
					this.branchTargetHeadQuaternions[i] = getQuaternionByAngle(0, 0, degToRad(branchConfig.pitch));
					this.branchTargetRollQuaternions[i] = getQuaternionByAngle(degToRad(branchConfig.roll), 0, 0);
				}
			}
		}

		// if (!('lumen' in this.configValues) || configValues.lumen !== this.configValues.lumen) {
		// 	const power = this.lightOn ? getLightPower(configValues.lumen, conf.defaultLightPower) : 0;
		// 	for (let i = 0; i < this.lights.length; i++) {
		// 		this.lights[i].power = power;
		// 	}
		// }

		this.configValues = configValues;
		return flags;
	}


	createOrUpdatePole(configValues) {

		// design is not pole
		if ('design' in configValues && configValues.design !== 'pole') {
			console.log('design is not pole', configValues.design);
		}

		const self = this.constructor;
		const model = self.getPoleModel(configValues);
		// console.log('createOrUpdatePole model', model);
		if (this.pole && this.pole.name === model.name) {
			return false;
		}
		if (this.pole && this.pole.parent) {
			this.pole.parent.remove(this.pole);
			console.log('POLE DIFF', this.pole.name, model.name);
		}
		const pole = model.clone(true);
		pole.name = model.name;
		this.pole = pole;
		for (let i = 0; i < this.branches.length; i++) {
			const branch = this.branches[i];
			if (branch.bracket.parent !== this.pole) {
				if (!self.singleBracket || i === 0) {
					this.pole.add(branch.bracket);
				}
			}
		}
		console.log('createOrUpdatePole', this.pole);
		if (configValues.design !== 'pole') {
			this.pole.material.visible = false;
		}
		return true;
	}

	//recursive update material parameters
	updateMaterialParameter(object, match = '', parameters = {}, exclude = null, configValues) {
		object.traverse((child) => {
				const childName = child.name.toLowerCase();
				if (
					(match === '' || childName.includes(match.toLowerCase())) &&
					(exclude === null || !exclude.some((e) => childName.toLowerCase().includes(e.toLowerCase())))
				) {
					if (child.isMesh && child.material) {
						// apply each parameter in "parameters" to cild.material
						for (const parameter in parameters) {
							if (parameters.hasOwnProperty(parameter)) {
								// if parameter name contains color, create a Color object
								if (parameter.toLowerCase().includes('color') || parameter.toLowerCase().includes('emissive')) {
									child.material[parameter] = new Color(parameters[parameter]);
								} else if (parameter.toLowerCase().includes('normalscale')) {
									//child.material.normalMap = this.normalMap;
									if (child.material.hasOwnProperty('normalScale')) {
										child.material[parameter].setScalar(parseFloat(parameters[parameter]));
									}
									//child.material.needsUpdate = true;
									//console.log('normalMap', childName, parameter, parseFloat(parameters[parameter]), child.material);
								} else {
									child.material[parameter] = parseFloat(parameters[parameter]);
								}
							}
						}
					}
				}
			}
		);
	}

	updateEmissiveParameter(object, environment = 'day', colourTemperature = '3000K', reflectorColor = 'silver') {

		const rc2c = {
			gold: new Color(colorStringToInt('#e5b867')),
			silver: new Color(colorStringToInt('#c0c0c0')),
			black: new Color(colorStringToInt('#303030')),
		};

		object.traverse((child) => {
				const childName = child.name.toLowerCase();

				if (child.isMesh && child.material && (childName.toLowerCase().includes('tir')) || childName.toLowerCase().includes('aussenreflektor') || childName.toLowerCase().includes('innenreflektor')) {
					child.material.color = rc2c[reflectorColor];
					child.material.emmisive = (environment === 'night') ? this.getLightColorFromKelvin(colourTemperature) : new Color(colorStringToInt('#2d2d2d'));
					child.material.emissiveIntensity = (environment === 'night') ? 0.8 : 0;
					child.material.opacity = 1;
					child.transparent = false;
					child.material.transmission = 0;
					child.material.reflectivity = (environment === 'night') ? 0.5 : 0.8;
					child.material.metalness = (environment === 'night') ? 0 : 0.9;
				}
				if (child.isMesh && child.material && (childName.toLowerCase().includes('prismenscheibe'))) {
					child.material.color = new Color(colorStringToInt('#505050'));
					child.material.opacity = 0.4;
					child.material.transmission = 0;
					child.material.transparent = true;
					child.material.clearcoat = 0;
					child.material.roughness = 0.1;
					child.material.thickness = 1.5;
					child.material.emmisive = 0;
					child.material.emissiveIntensity = 0;
					child.material.ior = 1.5;
					child.material.reflectivity = 0;
					child.material.metalness = 0;

					if (child.material.normalScale) {
						//console.log('normalMap prismenscheibe', childName, child.material, configValues);
						child.material.normalMap.flipY = false;
						if (child.material.hasOwnProperty('normalScale')) {
							child.material.normalScale.setScalar(1);
						}
						child.material.normalScale = new Vector2(1, -1);
						child.material.needsUpdate = true;
					}
				}
			}
		);
	}

	createOrUpdateLightBranch(index, configValues) {
		const self = this.constructor;
		const branchConfig = configValues.heads[index];
		const oldBranchConfig = 'heads' in this.configValues ? this.configValues.heads[index] : null;
		if (!branchConfig.active) {
			return;
		}

		const oldBranch = (index < this.branches.length && this.branches[index]) ?
			this.branches[index] : {
				bracket: null,
				bracketMesh: null,
				bracketArm: null,
				head: null,
				headLinks: [],
				reflectors: [],
				lightLink: null,
				number: null
			};

		// bracket
		const bracketModel = self.getBracketModel(configValues, index, branchConfig);
		let isNewBracket = false;
		let bracket = null;
		let headLinks = [];
		let bracketMesh = null;
		let bracketArm = null;
		let bracketSize = null;
		let bracketOffset = null;
		//console.log('createOrUpdateLightBranch', index, configValues);

		if (
			(!self.singleBracket || index === 0) && (
				!oldBranch.bracket || oldBranch.bracket.name !== bracketModel.name
			)
		) {
			if (oldBranch.bracket && oldBranch.bracket.parent) {
				oldBranch.bracket.parent.remove(oldBranch.bracket);
			}
			isNewBracket = true;
			bracket = bracketModel.clone(true);
			bracket.name = bracketModel.name;

			const originalPos = bracket.position.clone();
			const originalRotation = bracket.rotation.clone();

			if (self.checkCollisions) {
				bracket.position.set(0, 0, 0);
				bracket.rotation.set(0, 0, 0);
				bracket.updateMatrix();
				bracket.updateMatrixWorld();
				if (bracket.geometry) {
					bracket.geometry.computeBoundingBox();
				}
			}
			const bracketBox = new Box3();

			const bracketName = bracket.name;
			const bracketMeshName = (bracketName + conf.meshNameSuffix).toLowerCase();
			if (bracket.isMesh) {
				bracketMesh = bracket;
			}

			bracketArm = bracket.getObjectByName(bracketName + conf.armNameSuffix);

			// we need to separate the material for each one, otherwise changing the color to one
			// would change the color of all the others
			bracket.traverse((child) => {
				const childName = child.name.toLowerCase();
				if (!bracketMesh && childName === bracketMeshName) {
					bracketMesh = child;
				} else if (childName.indexOf('head_link') >= 0) {
					headLinks.push(child);
				}
				if (childName.match(conf.wallmountRegExp)) {
					child.visible = configValues.design !== 'pole';
				}
			});

			if (self.checkCollisions) {
				const info = this.getBracketCollisionBoxInfo(configValues, bracket, bracketMesh);
				bracketOffset = info.bracketOffset;
				bracketSize = info.bracketSize;
			}

			if (self.checkCollisions) {
				if (oldBranch.bracket) {
					bracket.position.copy(oldBranch.bracket.position);
					bracket.rotation.copy(oldBranch.bracket.rotation);
				} else {
					bracket.position.copy(originalPos);
					bracket.rotation.copy(originalRotation);
				}
			}
		} else {
			if (self.singleBracket && index > 0) {
				bracket = this.branches[0].bracket;
				bracketMesh = this.branches[0].bracketMesh;
				bracketArm = this.branches[0].bracketArm;
				headLinks = this.branches[0].headLinks;
				bracketSize = this.branches[0].bracketSize;
				bracketOffset = this.branches[0].bracketOffset;
			} else {
				bracket = oldBranch.bracket;
				bracketMesh = oldBranch.bracketMesh;
				bracketArm = oldBranch.bracketArm;
				headLinks = oldBranch.headLinks;
				bracketSize = oldBranch.bracketSize;
				bracketOffset = oldBranch.bracketOffset;
			}
		}

		if (!bracketArm) {
			console.log('BRACKET ARM NOT FOUND', index, bracket);
		}
		if (!bracketMesh) {
			console.log('BRACKET MESH NOT FOUND', index, bracket);
		}
		if (!headLinks.length) {
			console.log('HEAD LINK NOT FOUND', bracket);
		}
		bracket.userData.index = self.singleBracket ? 0 : index;

		// head
		const headModel = self.getHeadModel(configValues, branchConfig);
		let head = null;
		let number = null;
		let lightLink = null;
		let reflectors = [];
		let isNewHead = false;
		let headSize = null;
		let headOffset = null;
		const headBox = new Box3();

		if (
			!oldBranch.head ||
			oldBranch.head.name !== headModel.name ||
			!oldBranchConfig ||
			branchConfig.accessories !== oldBranchConfig.accessories ||
			branchConfig.modifier !== oldBranchConfig.modifier ||
			branchConfig.cap !== oldBranchConfig.cap ||
			branchConfig.type !== oldBranchConfig.type ||
			branchConfig.colourTemperature !== oldBranchConfig.colourTemperature ||
			configValues.colourTemperature !== this.configValues.colourTemperature ||
			branchConfig.lightDistribution !== oldBranchConfig.lightDistribution ||
			configValues.lightDistribution !== this.configValues.lightDistribution ||
			configValues.environment !== this.configValues.environment
		) {
			isNewHead = true;
			head = headModel.clone(true);
			head.name = headModel.name;
			head.position.set(0, 0, 0);
			head.updateMatrix();
			head.updateMatrixWorld();
			head.traverse((child) => {
				const headPart = child.userData.headPart;
				if (headPart) {
					child.visible = self.isHeadPartVisible(headPart, branchConfig);
					// console.log('HEAD', index, headPart, child.visible);
					const materialMethod = 'get' + headPart.substring(0, 1).toUpperCase() + headPart.substring(1) + 'Material';
					if (materialMethod in self) {
						//child.material = self[materialMethod](configValues.staticView);
					}
					if (headPart === 'reflector' || headPart === 'reflectorRgbw') {
						reflectors.push(child);
						// child.material.emissive.set(0xFFFFFF);
						//child.material.emissiveIntensity = this.lightOn ? conf.reflectorEmissionIntensity : 0;
					}
				} else {
					//console.log('Head Material', child.name, child.material, configValues.materialBody);
					//child.material = self.getRealisticMaterial(configValues.staticView);
				}

				if (!lightLink && child.name.match(conf.lightLinkRegExp)) {
					lightLink = child;
				}
				if (child.name.match(conf.pointLightRegExp)) {
					child.color = this.getLightColorFromKelvin(configValues.heads[index].colourTemperature);
					child.intensity = configValues.environment === 'night' ? 0.7 : 0;
					child.castShadow = false;
					child.distance = 0.2;
				}
				if (child.name.match(conf.spotLightRegExp)) {
					const ld = configValues.heads[index].lightDistribution;
					const ldo = iomoLightDistributionOptions.find((option) => option.id === ld);
					const angle = ldo.angle ?? 30;
					const intensity = ldo.intensity * 4 ?? 30;

					child.add(child.target); // move target inside the spotlight for relative angles

					child.color = this.getLightColorFromKelvin(configValues.heads[index].colourTemperature);
					child.intensity = configValues.environment === 'night' ? intensity : 0;
					child.distance = null;
					child.angle = angle * (Math.PI / 180);
					child.penumbra = 0.15;
					child.castShadow = false;
					child.shadow.mapSize.set(128, 128);
				}

				if (child.isMesh && child.visible) {
					child.receiveShadow = true;
					child.updateMatrix();
					child.updateMatrixWorld();
					child.geometry.computeBoundingBox();
					const box = new Box3();
					box.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
					headBox.union(box);
				}
			});

			// headBox.expandByScalar(0.01);
			headSize = new Vector3();
			headBox.getSize(headSize);
			headOffset = new Vector3();
			headBox.getCenter(headOffset);
			headOffset.x += self.isHeadSmall(head) ? 0.035 : 0.05;

			if (!lightLink) {
				console.log('NO LINK', index);
			}
			head.rotateZ(degToRad(conf.headPitch));

			number = new Object3D();
			bracket.add(number);
			number.translateX(0.25);
			number.translateY(0.15);
			number.visible = false;

			// number.rotateY(degToRad(180));
			if (oldBranch.head && oldBranch.head.parent) {
				oldBranch.head.parent.remove(oldBranch.head);
			}
		} else {
			head = this.branches[index].head;
			headSize = this.branches[index].headSize;
			headOffset = this.branches[index].headOffset;
			number = this.branches[index].number;
			lightLink = this.branches[index].lightLink;
			reflectors = this.branches[index].reflectors;
		}
		head.userData.index = index;


		if (self.singleBracket && index > 0) {
			bracketMesh = this.branches[0].bracketMesh;
		}
		if (head.parent !== bracketArm) {
			if (head.parent) {
				head.parent.remove(head);
			}
			bracketArm.add(head);
			const headLinkIndex = self.singleBracket ? Math.min(headLinks.length - 1, index) : 0;
			head.position.copy(headLinks[headLinkIndex].position);
			head.rotation.set(0, 0, 0);
			if (self.singleBracket && 'getHeadAngle' in self) {
				const angle = self.getHeadAngle(index, bracket.userData.id);
				head.rotateY(degToRad(angle));
			}
			head.rotateZ(degToRad(conf.headPitch));
		}

		if (
			isNewBracket ||
			isNewHead ||
			!('head' in this.configValues) ||
			configValues.color !== this.configValues.color ||
			configValues.colorInlay !== this.configValues.colorInlay ||
			configValues.heads[index].collisions.length !== this.configValues.heads[index].collisions.length
		) {
			//this.setColor(bracket, configValues.heads[index].collisions.length ? conf.modelOnCollisionColor : self.getBracketColor(configValues));
			//this.setColor(head, configValues.heads[index].collisions.length ? conf.modelOnCollisionColor : self.getHeadColor(configValues));
		}

		//set materials
		let materialBody = iomoMaterials.blackMatte.material;
		let materialRing = iomoMaterials.blackGloss.material;
		if (configValues.materialBody) materialBody = configValues.materialBody;
		if (configValues.materialRing) materialRing = configValues.materialRing;
		if (configValues.useTestMaterial) {
			materialBody = configValues.testMaterialBody;
			materialRing = configValues.testMaterialRing;
		}
		// console.log('materials', materialBody, materialRing);
		const exclude = ['Prismenscheibe', 'TIR', 'Reflektor', 'Innenreflektor', 'Light', 'Abschlussscheibe', 'Honeycomb', 'light', 'UX', 'GFX', 'Zentralabdeckung', 'Aussenreflektor', 'Wall'];
		this.updateMaterialParameter(bracket, '', materialRing, exclude, configValues);
		this.updateMaterialParameter(head, '', materialBody, exclude, configValues);
		this.updateMaterialParameter(head, 'koerper', materialRing, exclude, configValues);

		this.updateEmissiveParameter(head, configValues.environment, configValues.heads[index].colourTemperature, configValues.heads[index].reflectorColour);

		if (
			isNewBracket ||
			isNewHead
		) {
			const colourTemperature = branchConfig.colourTemperature !== null ? branchConfig.colourTemperature : configValues.colourTemperature;
			const lightColor = getLightColorFromTemperature(colourTemperature, conf.defaultColourTemperature);

			for (const reflector of reflectors) {
				reflector.material.emissive.copy(lightColor);
			}
		}

		this.branches[index] = {
			bracket: bracket,
			bracketMesh: bracketMesh,
			bracketArm: bracketArm,
			bracketSize: bracketSize,
			bracketOffset: bracketOffset,
			headLinks: headLinks,
			head: head,
			headSize: headSize,
			headOffset: headOffset,
			lightLink: lightLink,
			reflectors: reflectors,
			number: number
		};
	}

	getBracketCollisionBoxInfo(configValues, bracket, bracketMesh) {
		const self = this.constructor;
		let bracketOffset = null;
		let bracketSize = null;
		const bracketBox = new Box3();
		bracketBox.setFromObject(bracket);
		bracketSize = new Vector3();
		bracketBox.getSize(bracketSize);
		bracketOffset = new Vector3();
		bracketBox.getCenter(bracketOffset);
		bracketOffset.x -= bracketMesh.position.x;
		return {bracketOffset: bracketOffset, bracketSize: bracketSize};
	}

	updateHeadsPositions(elapsedTime = null) {
		const immediate = elapsedTime === null;
		const self = this.constructor;
		const baseRatio = self.requireCutouts ? 1 : 0.09;
		const translateRatio = immediate ? 1 : Math.min(1, baseRatio * elapsedTime * 60 / 1000);
		const minDistance = 0.005;

		const rotateRatio = immediate ? 1 : Math.min(1, baseRatio * elapsedTime * 60 / 1000);
		const minAngle = degToRad(1);

		let changed = false;
		for (let i = 0; i < this.branches.length; i++) {
			const branch = this.branches[i];
			if (self.singleBracket) {
				if (i === 0) {
					let headLink = null;
					this.pole.traverse((item) => {
						if (!headLink && item.name.length && item.name.match(conf.topLinkRegExp)) {
							headLink = item;
						}
					});
					if (headLink) {
						branch.bracket.position.y = headLink.position.y;
					} else {
						throw Error('missing top link');
						// branch.bracket.position.y = this.configValues.poleHeight;
					}
				}
			} else {
				const head = this.configValues.heads[i];
				// vertical position
				if (head.active) {
					const offsets = this.verticalOffsets[i];
					if (branch.bracket.position.y !== offsets.actualHeight) {
						// changed = true;
						const finalValue = offsets.actualHeight;
						const currentValue = branch.bracket.position.y;
						const distance = finalValue - currentValue;
						if (Math.abs(distance) <= minDistance) {
							branch.bracket.position.y = finalValue;
						} else {
							branch.bracket.position.y += distance * translateRatio;
							changed = true;
						}
					}
					// horizontal position: the poles may be conical
					const x = this.branchHorizontalOffsets[i];
					if (branch.bracketMesh.position.x !== x) {
						const distance = x - branch.bracketMesh.position.x;
						if (Math.abs(distance) <= minDistance) {
							branch.bracketMesh.position.x = x;
						} else {
							changed = true;
							branch.bracketMesh.position.x += distance * translateRatio;
						}
					}

					// rotation around the pole
					//branch.bracket.applyQuaternion(this.branchTargetQuaternions[i]);
					//branch.head.applyQuaternion(this.branchTargetHeadQuaternions[i]);
					//branch.bracketArm.quaternion.applyQuaternion(this.branchTargetRollQuaternions[i]);


					const angleDistance = branch.bracket.quaternion.angleTo(this.branchTargetQuaternions[i]);
					if (radToDeg(angleDistance) > 0.25) {
						changed = true;
						branch.bracket.quaternion.rotateTowards(
							this.branchTargetQuaternions[i],
							angleDistance <= minAngle ? angleDistance : angleDistance * rotateRatio
						);
					}
					// head Pitch
					const pitchDistance = branch.head.quaternion.angleTo(this.branchTargetHeadQuaternions[i]);
					if (radToDeg(pitchDistance) > 0.25) {
						changed = true;
						branch.head.quaternion.rotateTowards(
							this.branchTargetHeadQuaternions[i],
							pitchDistance <= minAngle ? pitchDistance : pitchDistance * rotateRatio
						);
					}
					// head Roll
					const rollDistance = branch.bracketArm.quaternion.angleTo(this.branchTargetRollQuaternions[i]);
					if (radToDeg(rollDistance) > 0.25) {
						changed = true;
						branch.bracketArm.quaternion.rotateTowards(
							this.branchTargetRollQuaternions[i],
							rollDistance <= minAngle ? rollDistance : rollDistance * rotateRatio
						);
					}

					branch.head.traverse((child) => {
						if (child.name.match(conf.capAsymRegExp)) {
							child.rotation.z = (head.roll + 90) * (Math.PI / 180);
						}
					});

				}
			}
		}
		return changed;
	}


	isFeatureChanged(configValues, name) {
		return !(name in this.configValues && this.configValues[name] === configValues[name]);
	}


	setColor(parent, color, traverse = true, parts = true) {
		if (traverse) {
			parent.traverse((item) => this.setItemColor(item, color));
		} else {
			this.setItemColor(parent, color);
			if (parts) {
				const modelType = parent.userData.modelType;
				for (const child of parent.children) {
					if (!('modelType' in child.userData) || child.userData.modelType === modelType) {
						this.setItemColor(child, color);
					}
				}
			}
		}
	}


	setItemColor(item, color) {
		if (
			item.isMesh &&
			item.material
		) {
			// objects after boolean operations have an array of materials, instead of one
			const materials = Array.isArray(item.material) ? item.material : [item.material];
			for (const material of materials) {
				if (material.userData && material.userData.colored) {
					material.color.set(color);
				}
			}
			item.castShadow = true;
			item.receiveShadow = true;
		}
	}


	getPole() {
		return this.pole;
	}

	getBranches() {
		return this.branches;
	}

	getConfigValues() {
		return this.configValues;
	}

	getVerticalOffsets() {
		return this.verticalOffsets;
	}

	getLightColorFromKelvin(kelvin) {
		if (typeof kelvin !== 'number') {
			kelvin = parseInt(kelvin, 10);
		}
		const lightColor = new Color();
		setKelvin(lightColor, kelvin);
		return lightColor;
	}
}

export {IomoLuminaire};

